/**
 * Users API – list, approve, reject, invite, deactivate, activate, delete, update (Phase 1 Week 3).
 * All calls require Authorization header with valid JWT.
 * On 401, triggers global auth failure handler (logout + redirect to login).
 */

import type {
  ListUsersResponse,
  ListUserSummariesResponse,
  ApproveUserRequest,
  ApproveUserResponse,
  RejectUserRequest,
  RejectUserResponse,
  InviteUserRequest,
  InviteUserResponse,
  UpdateUserRequest,
  UpdateUserResponse,
  ImportReturningFamiliesRequest,
  ImportReturningFamiliesResponse,
  ClearReturningFamiliesRequest,
  ClearReturningFamiliesResponse,
  SendPasswordResetResponse,
  GetUserResponse,
  UserMemberDetailResponse,
  UserActionResponse,
  DeleteUserResponse,
} from '@lambda/shared/types';
import { apiRequest } from './apiClient';

export interface ListUsersParams {
  /** Single status or comma-separated for multiple (e.g. "active,inactive") */
  status?: string | string[];
  /** Single role or comma-separated for multiple */
  role?: string | string[];
  search?: string;
  limit?: number;
  /** 1-based page (offset pagination). */
  page?: number;
  sortBy?: 'name' | 'role' | 'status' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  /** When true, response includes pendingApprovalUsers for the approval card. */
  includePendingQueue?: boolean;
}

export const usersApi = {
  list(accessToken: string, params?: ListUsersParams) {
    const searchParams = new URLSearchParams();
    if (params?.status != null) {
      const s = Array.isArray(params.status) ? params.status.filter(Boolean).join(',') : params.status;
      if (s) searchParams.set('status', s);
    }
    if (params?.role != null) {
      const r = Array.isArray(params.role) ? params.role.filter(Boolean).join(',') : params.role;
      if (r) searchParams.set('role', r);
    }
    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit != null) searchParams.set('limit', String(params.limit));
    if (params?.page != null) searchParams.set('page', String(params.page));
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params?.includePendingQueue) searchParams.set('includePendingQueue', '1');
    const qs = searchParams.toString();
    return apiRequest<ListUsersResponse>(
      `/users${qs ? `?${qs}` : ''}`,
      accessToken,
      { method: 'GET' }
    );
  },

  /**
   * Drain every page for a list filter (server caps limit at 100).
   * Use when a UI needs the full matching set (tutor picker, mentionable reviewers).
   * Newest users sort last (createdAt asc) so a mid-drain signup cannot shift earlier pages.
   */
  async listAllPages(accessToken: string, params?: Omit<ListUsersParams, 'page' | 'limit'>) {
    const limit = 100;
    const maxPages = 50;
    const users: NonNullable<ListUsersResponse['users']> = [];
    const seen = new Set<string>();
    let page = 1;
    for (;;) {
      const res = await usersApi.list(accessToken, {
        ...params,
        limit,
        page,
        sortBy: params?.sortBy ?? 'createdAt',
        sortOrder: params?.sortOrder ?? 'asc',
      });
      for (const u of res.data?.users ?? []) {
        if (seen.has(u.userId)) continue;
        seen.add(u.userId);
        users.push(u);
      }
      const totalPages = res.data?.totalPages ?? 1;
      if (page >= totalPages || page >= maxPages) break;
      page += 1;
    }
    return users;
  },

  /** Batch display fields for specific user IDs (avoids /users list pagination). */
  getSummaries(accessToken: string, userIds: string[]) {
    const ids = [...new Set(userIds.map((id) => id.trim()).filter(Boolean))];
    const searchParams = new URLSearchParams();
    searchParams.set('ids', ids.join(','));
    return apiRequest<ListUserSummariesResponse>(
      `/users/summaries?${searchParams.toString()}`,
      accessToken,
      { method: 'GET' }
    );
  },

  approve(accessToken: string, data: ApproveUserRequest) {
    return apiRequest<ApproveUserResponse>('/users/approve', accessToken, {
      method: 'POST',
      payload: data,
    });
  },

  reject(accessToken: string, data: RejectUserRequest) {
    return apiRequest<RejectUserResponse>('/users/reject', accessToken, {
      method: 'POST',
      payload: data,
    });
  },

  invite(accessToken: string, data: InviteUserRequest) {
    return apiRequest<InviteUserResponse>('/users/invite', accessToken, {
      method: 'POST',
      payload: data,
    });
  },

  deactivate(accessToken: string, userId: string) {
    return apiRequest<UserActionResponse>('/users/deactivate', accessToken, {
      method: 'POST',
      payload: { userId },
    });
  },

  activate(accessToken: string, userId: string) {
    return apiRequest<UserActionResponse>('/users/activate', accessToken, {
      method: 'POST',
      payload: { userId },
    });
  },

  delete(accessToken: string, userId: string) {
    return apiRequest<DeleteUserResponse>('/users/delete', accessToken, {
      method: 'POST',
      payload: { userId },
    });
  },

  update(accessToken: string, userId: string, data: UpdateUserRequest) {
    return apiRequest<UpdateUserResponse>(`/users/${encodeURIComponent(userId)}`, accessToken, {
      method: 'PUT',
      payload: data,
    });
  },

  importReturningFamilies(accessToken: string, data: ImportReturningFamiliesRequest) {
    return apiRequest<ImportReturningFamiliesResponse>('/users/returning-families/import', accessToken, {
      method: 'POST',
      payload: data,
    });
  },

  clearReturningFamilies(accessToken: string, data: ClearReturningFamiliesRequest) {
    return apiRequest<ClearReturningFamiliesResponse>('/users/returning-families/clear', accessToken, {
      method: 'POST',
      payload: data,
    });
  },

  get(accessToken: string, userId: string) {
    return apiRequest<GetUserResponse>(
      `/users/${encodeURIComponent(userId)}`,
      accessToken,
      { method: 'GET' }
    );
  },

  getMemberDetail(accessToken: string, userId: string) {
    return apiRequest<UserMemberDetailResponse>(
      `/users/${encodeURIComponent(userId)}/member-detail`,
      accessToken,
      { method: 'GET' }
    );
  },

  sendPasswordReset(accessToken: string, userId: string) {
    return apiRequest<SendPasswordResetResponse>(
      `/users/${encodeURIComponent(userId)}/send-password-reset`,
      accessToken,
      { method: 'POST' }
    );
  },

  profileImageUploadRequest(
    accessToken: string,
    userId: string,
    fileType: string,
    fileSize: number
  ) {
    return apiRequest<{ uploadUrl: string; fileKey: string; message: string }>(
      `/users/${encodeURIComponent(userId)}/profile-image/upload-request`,
      accessToken,
      { method: 'POST', payload: { fileType, fileSize } }
    );
  },

  profileImageConfirm(accessToken: string, userId: string) {
    return apiRequest<UpdateUserResponse>(
      `/users/${encodeURIComponent(userId)}/profile-image/confirm`,
      accessToken,
      { method: 'POST', payload: {} }
    );
  },

  profileImageRemove(accessToken: string, userId: string) {
    return apiRequest<UpdateUserResponse>(
      `/users/${encodeURIComponent(userId)}/profile-image`,
      accessToken,
      { method: 'DELETE' }
    );
  },
};
